import type { ApiSpec, Audience, ReadOrWrite, RepoApiSpec } from '../App.types';

export enum Api {
    Useradmin = 'useradmin',
    Assets = 'asset-administration',
    DrivingTime = 'drivingtime',
    Tacho = 'tachograph-services',
    Maintenance = 'servicecare',
    DriverAdmin = 'driver-administration',
    IotEventOutboundFeed = 'iot-events',
    Perform = 'perform',
    TransportOrderHub = 'transportorder-hub',
    ShipmentTracking = 'shipment-tracking',
    ManDataPackage = 'man-datapackage',
}

export const getApiFromString = (apiName: string) => {
    switch (apiName) {
        case 'useradmin':
            return Api.Useradmin;
        case 'asset-administration':
            return Api.Assets;
        case 'drivingtime':
            return Api.DrivingTime;
        case 'tachograph-services':
            return Api.Tacho;
        case 'servicecare':
            return Api.Maintenance;
        case 'driver-administration':
            return Api.DriverAdmin;
        case 'iot-events':
            return Api.IotEventOutboundFeed;
        case 'perform':
            return Api.Perform;
        case 'transportorder-hub':
            return Api.TransportOrderHub;
        case 'shipment-tracking':
            return Api.ShipmentTracking;
        case 'man-datapackage':
            return Api.ManDataPackage;
        default:
            return undefined;
    }
};

const knownApis: ApiSpec[] = [
    {
        name: Api.Useradmin,
        scopes: [
            { accessType: 'read', name: 'user.read' },
            { accessType: 'write', name: 'user.write' },
        ],
        audience: 'component-internal',
    },
    {
        name: Api.Assets,
        scopes: [
            { accessType: 'read', name: 'asset-administration.read' },
            { accessType: 'write', name: 'asset-administration.write' },
        ],
        permissions: [
            { accessType: 'read', name: 'asset.read' },
            { accessType: 'write', name: 'asset.write' },
        ],
        audience: 'external-partner',
    },
    {
        name: Api.DrivingTime,
        scopes: [{ accessType: 'read', name: 'driving-time.read' }],
        permissions: [{ accessType: 'read', name: 'driving-time.read' }],
        audience: 'external-partner',
    },
    {
        name: Api.Tacho,
        scopes: [{ accessType: 'read', name: 'tachograph-partner.read' }],
        permissions: [
            { accessType: 'read', name: 'file-list.read' },
            { accessType: 'read', name: 'file-data.read' },
        ],
        audience: 'external-partner',
        legacy: true,
    },
    {
        name: Api.Maintenance,
        scopes: [
            { accessType: 'read', name: 'vehiclestate.read' },
            { accessType: 'write', name: 'vehiclestate.write' },
        ],
        permissions: [
            { accessType: 'read', name: 'read-write' },
            { accessType: 'write', name: 'read-write' },
        ],
        audience: 'external-partner',
    },
    {
        name: Api.DriverAdmin,
        scopes: [
            { accessType: 'read', name: 'drivers.read' },
            { accessType: 'write', name: 'drivers.write' },
        ],
        permissions: [
            { accessType: 'read', name: 'driver.read' },
            { accessType: 'write', name: 'driver.create' },
            { accessType: 'write', name: 'driver.update' },
            { accessType: 'write', name: 'driver.delete' },
        ],
        audience: 'external-partner',
    },
    {
        name: Api.IotEventOutboundFeed,
        scopes: [{ accessType: 'read', name: 'iot-events.read' }],
        permissions: [{ accessType: 'read', name: 'iot-events.read' }],
        audience: 'external-partner',
    },
    {
        name: Api.Perform,
        scopes: [
            { accessType: 'read', name: 'perform.read' },
            { accessType: 'read', name: 'operating-conditions.read' },
            { accessType: 'read', name: 'operating-conditions.write' },
            { accessType: 'read', name: 'perform.partner-api.read' },
        ],
        additionalRequiredApis: [
            { api: Api.Assets, accessType: ['read'] },
            { api: Api.DriverAdmin, accessType: ['read'] },
        ],
        audience: 'external-partner',
    },
    {
        name: Api.ShipmentTracking,
        scopes: [{ accessType: 'write', name: 'transportstatus.write' }],
        permissions: [{ accessType: 'write', name: 'transport-status.write' }],
        audience: 'external-partner',
        hidden: true,
    },
    {
        name: Api.TransportOrderHub,
        scopes: [
            { accessType: 'read', name: 'transport-order.read' },
            { accessType: 'read', name: 'logistics.freight-forwarder' },
            { accessType: 'write', name: 'transport-order.confirm' },
        ],
        additionalRequiredApis: [{ api: Api.ShipmentTracking, accessType: ['write'] }],
        permissions: [
            { accessType: 'read', name: 'transportorder.read' },
            { accessType: 'write', name: 'transportorder.confirm' },
        ],
        audience: 'external-partner',
    },
    {
        name: Api.ManDataPackage,
        scopes: [{ accessType: 'read', name: 'man-datapackage.read' }],
        audience: 'external-partner',
    },
];

const onlyUnique = <T>(value: T, index: number, self: T[]) => self.indexOf(value) === index;

const getApiSpecByName = (apiName: Api, type: ReadOrWrite[]): RepoApiSpec | undefined => {
    const selectedApi = knownApis.find(api => api.name === apiName);
    if (selectedApi === undefined) {
        return;
    }
    return {
        name: selectedApi.name,
        scopes: selectedApi.scopes
            .filter(scope => type.includes(scope.accessType))
            .map(filteredScope => filteredScope.name),
        permissions: selectedApi.permissions
            ? selectedApi.permissions
                  .filter(perm => type.includes(perm.accessType))
                  .map(filteredPerm => filteredPerm.name)
            : undefined,
        additionalRequiredApis: selectedApi.additionalRequiredApis,
    };
};

const getAvailableAccessTypes = (apiName: Api) => {
    const selectedApi = knownApis.find(api => api.name === apiName);
    if (selectedApi === undefined) {
        return;
    }
    return selectedApi.scopes.map(scope => scope.accessType).filter(onlyUnique);
};

const getApiSpecByAudience = (audience: Audience) =>
    knownApis
        .filter(api => api.audience === audience)
        .filter(api => !api.hidden)
        .map(api => ({
            name: api.name,
        }));

export const ApiRepository = {
    getApiSpecByName,
    getApiSpecByAudience,
    getAvailableAccessTypes,
};
